import {MediaFile, useProductOptions} from '@shopify/hydrogen';
import {
  MediaContentType,
  MediaImage,
} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import {useEffect, useState} from 'react';
import type {ProductWithNodes} from '../../types';

type Props = {
  storefrontProduct: ProductWithNodes;
};

const MODEL_3D_PROPS = {
  interactionPromptThreshold: '0',
};

export default function ProductGallery({storefrontProduct}: Props) {
  const media = storefrontProduct?.media?.nodes;

  const [isZoomed, setIsZoomed] = useState<Boolean>(false)
  const [img, setImg] = useState<any | undefined>()
  const [windowWidth, setWindowWidth] = useState<number>(0)

  useEffect(() => {
    setWindowWidth(window.innerWidth)
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleZoom = (data: any) => {
    if (windowWidth >= 1024 && data.mediaContentType === 'IMAGE') {
      setIsZoomed(!isZoomed)
      setImg(data)
    }
  }

  const {selectedVariant} = useProductOptions();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    draggable: windowWidth >= 1024 ? false : true,
    loop: true,
    skipSnaps: true,
    speed: 7,
  });

  const handleNext = () => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  };

  const handlePrevious = () => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }; 

  useEffect(() => {
    if (!selectedVariant) {
      return;
    }

    const variantImageUrl = selectedVariant?.image?.url?.split('?')[0];
    const galleryIndex =
      media?.findIndex((mediaItem) => {
        if (mediaItem.mediaContentType === MediaContentType.Image) {
          return (
            (mediaItem as MediaImage)?.image?.url.split('?')[0] ===
            variantImageUrl
          );
        }
        return false;
      }) ?? -1;

    if (emblaApi && galleryIndex >= 0) {
      emblaApi.scrollTo(galleryIndex, true)
    }
  }, [emblaApi, media, selectedVariant]);

  if (!media?.length) {
    return (
      <div className="px-10 overflow-hidden w-full" ref={emblaRef}>
        <div className="px-10 mb-20 pt-edit flex w-full">
          {[...Array(4)].map((i) =>
            <div
              key={i}
              className={clsx(
                'relative flex w-full bg-lightGray shrink-0 grow-0 select-none object-cover mr-5',
                'sm:h-[calc(calc(100vw-25px)/2)] sm:w-[calc(calc(100vw-25px)/2)]',
                'lg:h-[calc(calc(100vw-35px)/4)] lg:w-[calc(calc(100vw-35px)/4)]',
                'cursor-zoom-in'
              )} 
              >
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'relative bg-white px-10 mb-20 pt-edit'
      )}
      tabIndex={-1}
    >
      {isZoomed &&
        <div
          className="fixed inset-0 z-[9999] h-screen w-screen bg-white overflow-y-scroll"
          onClick={() => handleZoom(img)}
        >
          <MediaFile
            // @ts-expect-error options should accept className
            className={clsx(
              'relative w-full',
              'cursor-zoom-out'
            )}
            data={img}
            alt={storefrontProduct.title}
            draggable={false}
            fetchpriority="high"
            key={img.id}
            options={{crop: 'center'}}
            tabIndex={0}       
          />
          {img?.image?.altText &&
            <div className={clsx(
              'fixed bottom-5 left-5 w-full'
            )}>
              <span className="bg-highlight px-5">
                {img.image.altText}
              </span>
            </div>
          }
        </div>
      }
      <div className="overflow-hidden w-full" ref={emblaRef}>
        <div className="flex w-full">
          {[...Array(2)].map(() => // LOOP FALSE
            media.map((med: any) => { // ANY !!!!!!!!!!!
              let extraProps = {};
              if (med.mediaContentType === MediaContentType.Model_3D) {
                extraProps = MODEL_3D_PROPS;
              }
              return (
                <div
                  key={med.id}
                  className={clsx(
                    'relative flex w-full shrink-0 grow-0 select-none object-cover mr-5',
                    'sm:h-[calc(calc(100vw-25px)/2)] sm:w-[calc(calc(100vw-25px)/2)]',
                    'lg:h-[calc(calc(100vw-35px)/4)] lg:w-[calc(calc(100vw-35px)/4)]',
                    'cursor-zoom-in'
                  )}
                >
                  <MediaFile
                    // @ts-expect-error options should accept className
                    className={clsx(
                      'cursor-zoom-in'
                    )}
                    data={med}
                    alt={storefrontProduct.title}
                    draggable={false}
                    fetchpriority="high"
                    key={med.id}
                    options={{crop: 'center'}}
                    tabIndex={0}
                    onClick={() => handleZoom(med)}
                    {...extraProps}
                  />
                  {med.mediaContentType === MediaContentType.Image && med?.image?.altText &&
                    <div className={clsx(
                      'absolute bottom-5 left-5 truncate z-50',
                      'w-[calc(calc(100vw-30px))]',
                      'sm:w-[calc(calc(100vw-45px)/2)]',
                      'lg:w-[calc(calc(100vw-75px)/4)]'
                    )}>
                      <span className="bg-highlight px-5">
                        {med?.image?.altText}
                      </span>
                    </div>
                  }
                </div>
              );
            })
          )}
        </div>
      </div>
      {media.length > 1 && (
        <>
          <div
            className="absolute left-0 top-0 h-full p-10 flex items-center cursor-w-resize"
            onClick={handlePrevious}
          >
            <span className="ml-10 text-small select-none scale-[1.15]">←</span>
          </div>
          <div
            className="absolute right-0 top-0 h-full p-10 flex items-center cursor-e-resize"
            onClick={handleNext}
          >
            <span className="mr-10 text-small select-none scale-[1.15]">→</span>
          </div>
        </>
      )}
    </div>
  );
}
